import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { capitalize } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  getDefaultProductMatchingFilter,
  ProductMatchingFilter,
} from '../../interfaces/SynchronizationSummary/ProductMatchingFilter';
import { PartnerPlatform } from '../../interfaces/PartnerPlatform/PartnerPlatform';
import {
  ExternalProductMatching,
  ExternalProductMatchings,
} from '../../interfaces/SynchronizationSummary/ExternalProductMatching';
import { getProductMatchingsByPlatform } from '../../api/SynchronizationSummary';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { ExternalProductMatchingTable } from './ExternalProductMatchingTable';
import { Pagination } from '../../interfaces/Pagination';
import {
  ProductIdentifierUpdate,
  transformProductIdentifierUpdate,
} from '../../interfaces/ProductIdentifiers/ProductIdentifiers';
import { updateProductIdentifiers } from '../../api/Product';
import { FilterSearchBox } from '../../components/SearchBox/FilterSearchBox';
import { getMatchingFieldName } from './utils';
import {
  getActivePartnerPlatformSyncJobsByPlatform,
  startPartnerPlatformSyncJob,
  streamPartnerPlatformSyncJob,
} from '../../api/PartnerPlatformSyncJob';
import { PartnerPlatformSyncJob } from '../../interfaces/PartnerPlatform/PartnerPlatformSyncJob';
import { SyncJobButton } from './SyncJobButton';

export const ExternalProductMatchingPage = () => {
  const navigate = useNavigate();

  const { platform } = useParams<string>();
  const partnerPlatform = platform ? (platform as PartnerPlatform) : 'trendyol';

  const [filter, setFilter] = useState(
    getDefaultProductMatchingFilter(partnerPlatform),
  );
  const [loading, setLoading] = useState(false);
  const [startingSync, setStartingSync] = useState(false);
  const [syncJob, setSyncJob] = useState<PartnerPlatformSyncJob | undefined>(
    undefined,
  );
  const [updating, setUpdating] = useState(false);
  const [matchings, setMatchings] = useState<ExternalProductMatchings>({
    data: [],
    totalCount: 0,
    matchingCount: 0,
  });
  const [updates, setUpdates] = useState<ProductIdentifierUpdate[]>([]);

  const refreshMatchings = (filters?: ProductMatchingFilter) => {
    setLoading(true);
    getProductMatchingsByPlatform(filters ?? filter)
      .then(setMatchings)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshMatchings();

    getActivePartnerPlatformSyncJobsByPlatform(partnerPlatform)
      .then((jobs) => setSyncJob(jobs[0]))
      .catch((err) => toast.error(err.message));
  }, [partnerPlatform]);

  useEffect(() => {
    if (!syncJob?.id) {
      return undefined;
    }

    return streamPartnerPlatformSyncJob(syncJob.id, {
      onMessage: setSyncJob,
      onError: toast.error,
    });
  }, [syncJob?.id]);

  const handleSearch = () => {
    const newFilter: ProductMatchingFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshMatchings(newFilter);
  };

  const handleChangePagination = useCallback(
    (newPagination: Pagination) => {
      const newFilter = { ...filter, pagination: newPagination };

      setFilter(newFilter);
      refreshMatchings(newFilter);
    },
    [filter],
  );

  const handleUpdateIdentifier = (
    matching: ExternalProductMatching,
    updatedValue: string,
  ) => {
    if (!matching.productID) return;

    setUpdates([
      ...updates.filter(
        (x) =>
          x.productID !== matching.productID ||
          x.optionValueID !== matching.optionValueID,
      ),
      ...(matching.existingID === updatedValue
        ? []
        : [
            {
              productID: matching.productID,
              optionValueID: matching.optionValueID,
              value: updatedValue,
            },
          ]),
    ]);
  };

  const handleSaveUpdates = () => {
    setUpdating(true);
    updateProductIdentifiers(
      updates.map((x) => transformProductIdentifierUpdate(x, partnerPlatform)),
    )
      .then(() => {
        toast.success('Ürün bilgileri başarıyla güncellendi');
        setUpdates([]);
        refreshMatchings();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setUpdating(false));
  };

  const handleStartSync = useCallback(() => {
    setStartingSync(true);
    startPartnerPlatformSyncJob(partnerPlatform)
      .then((job) => {
        setSyncJob(job);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setStartingSync(false));
  }, [partnerPlatform]);

  const goBack = () => navigate('/products/synchronization-summary');

  return (
    <Box sx={{ ...sharedStyles.pageBody, gap: 1.5 }}>
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>

        <Typography variant='h5'>
          {capitalize(platform)} Ürün Eşleşmeleri
        </Typography>

        <SyncJobButton
          isStarting={startingSync}
          syncJob={syncJob}
          onStart={handleStartSync}
          sx={{ ...sharedStyles.buttonText, marginLeft: 'auto' }}
        />
      </Box>

      {updates.length > 0 && (
        <Alert
          severity='info'
          action={
            updating ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                size='small'
                variant='contained'
                startIcon={<SaveIcon />}
                onClick={handleSaveUpdates}
                sx={{ ...sharedStyles.buttonText }}
              >
                Kaydet
              </Button>
            )
          }
        >
          {updates.length} adet değişiklik mevcut
        </Alert>
      )}

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FilterSearchBox
            label={getMatchingFieldName(partnerPlatform)}
            filters={filter}
            onChange={setFilter}
            fieldName='identifier'
            onEnter={handleSearch}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  partnerPlatform,
                  identifier: '',
                  pagination: filter.pagination,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Eşleşme Ara
            </Button>
          </Box>
        </Box>
      </Paper>

      <ExternalProductMatchingTable
        externalProductMatches={matchings.data}
        updates={updates}
        onUpdateIdentifier={handleUpdateIdentifier}
        partnerPlatform={partnerPlatform}
        totalCount={matchings.totalCount}
        pagination={filter.pagination}
        onPagination={handleChangePagination}
      />
    </Box>
  );
};
